import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  ArrowLeft, ArrowRight, Check, Link2, Image, Sparkles, DollarSign,
  Rocket, Loader2, X, Upload, RefreshCw, Eye, Save, AlertCircle, CheckCircle
} from 'lucide-react';
import { AdminLayout } from '../components/admin/AdminLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { id: 1, name: 'Lien', icon: Link2, description: 'Coller le lien source' },
  { id: 2, name: 'Photos', icon: Image, description: 'Ajouter les photos' },
  { id: 3, name: 'Contenu IA', icon: Sparkles, description: 'Nom, description, tags' },
  { id: 4, name: 'Prix', icon: DollarSign, description: 'Prix et variantes' },
  { id: 5, name: 'Publier', icon: Rocket, description: 'Finaliser' }
];

export function AdminImportWizardPage() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState('');
  const [productId, setProductId] = useState(null);
  
  // Step 1 data
  const [sourceUrl, setSourceUrl] = useState('');
  const [extractedData, setExtractedData] = useState(null);
  
  // Step 2 data
  const [imageUrls, setImageUrls] = useState('');
  const [uploadedImages, setUploadedImages] = useState([]);
  
  // Step 3 data
  const [aiContent, setAiContent] = useState({
    name_fr: '', name_en: '', name_he: '',
    description_fr: '', description_en: '', description_he: '',
    tags: []
  });
  const [tagsInput, setTagsInput] = useState('');
  
  // Step 4 data
  const [pricing, setPricing] = useState({
    source_price: '',
    margin_multiplier: 2.5,
    selling_price: 0
  });
  
  // Step 5 data
  const [publishStatus, setPublishStatus] = useState('draft');
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');

  useEffect(() => {
    const storedToken = localStorage.getItem('admin_token');
    if (storedToken) {
      setToken(storedToken);
      fetchCategories(storedToken);
    } else {
      navigate('/admin');
    }
  }, [navigate]);

  const fetchCategories = async (authToken) => {
    try {
      const res = await fetch(`${API_URL}/api/categories`);
      if (res.ok) {
        const data = await res.json();
        setCategories(data.categories || data || []);
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  // Step 1: Extract link
  const handleStep1 = async () => {
    if (!sourceUrl.trim()) {
      toast.error('Entrez une URL valide');
      return;
    }
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/sourcing/wizard/step1`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ url: sourceUrl })
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        if (data.error === 'duplicate') {
          toast.error(`Ce produit existe déjà: ${data.existing_product_name}`);
        } else {
          toast.error(data.detail || 'Erreur extraction');
        }
        return;
      }
      
      setProductId(data.product_id);
      setExtractedData(data);
      
      // Pré-remplir les images scrapées
      if (data.scraped?.images?.length > 0) {
        setImageUrls(data.scraped.images.join('\n'));
        toast.success(`${data.scraped.images_count} photo(s) trouvée(s) automatiquement!`);
      }
      
      // Pré-remplir le prix si scrapé
      if (data.scraped?.price_cny) {
        setPricing(prev => ({ ...prev, source_price: data.scraped.price_cny.toString() }));
      }
      
      // Message de succès avec détails du scraping
      if (data.scraped?.success) {
        toast.success(`Données récupérées: "${data.scraped.title?.substring(0, 40)}..."`);
      } else if (data.scraped?.error) {
        toast.warning(`Scraping partiel: ${data.scraped.error}`);
      }
      
      setCurrentStep(2);
    } catch (err) {
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Add photos
  const handleStep2 = async () => {
    const urls = imageUrls.split('\n').filter(u => u.trim().startsWith('http'));
    
    if (urls.length === 0) {
      toast.warning('Aucune URL de photo valide. Continuez sans photos ou ajoutez-en.');
      setCurrentStep(3);
      return;
    }
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/sourcing/wizard/step2`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ product_id: productId, image_urls: urls })
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        toast.error(data.detail || 'Erreur upload photos');
        return;
      }
      
      setUploadedImages(data.images || []);
      toast.success(`${data.images_uploaded} photo(s) importée(s)`);
      if (data.errors?.length) {
        data.errors.forEach(e => toast.warning(e));
      }
      setCurrentStep(3);
    } catch (err) {
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Generate AI content
  const handleGenerateAI = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/sourcing/wizard/step3-generate?product_id=${productId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        toast.error(data.detail || 'Erreur génération IA');
        return;
      }
      
      setAiContent({
        name_fr: data.name.fr,
        name_en: data.name.en,
        name_he: data.name.he,
        description_fr: data.description.fr,
        description_en: data.description.en,
        description_he: data.description.he,
        tags: data.tags
      });
      setTagsInput(data.tags.join(', '));
      toast.success('Contenu généré par Claude IA');
    } catch (err) {
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const handleStep3 = async () => {
    if (!aiContent.name_fr.trim()) {
      toast.error('Le nom français est requis');
      return;
    }
    
    setLoading(true);
    try {
      const tags = tagsInput.split(',').map(t => t.trim().toLowerCase()).filter(Boolean);
      
      const res = await fetch(`${API_URL}/api/sourcing/wizard/step3`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          product_id: productId,
          name_fr: aiContent.name_fr,
          name_en: aiContent.name_en || aiContent.name_fr,
          name_he: aiContent.name_he || aiContent.name_fr,
          description_fr: aiContent.description_fr,
          description_en: aiContent.description_en || aiContent.description_fr,
          description_he: aiContent.description_he || aiContent.description_fr,
          tags
        })
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        toast.error(data.detail || 'Erreur sauvegarde');
        return;
      }
      
      toast.success('Contenu sauvegardé');
      setCurrentStep(4);
    } catch (err) {
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  // Step 4: Pricing
  const handleStep4 = async () => {
    if (!pricing.source_price || pricing.source_price <= 0) {
      toast.error('Le prix source est requis');
      return;
    }
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/sourcing/wizard/step4`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          product_id: productId,
          source_price: parseFloat(pricing.source_price),
          margin_multiplier: parseFloat(pricing.margin_multiplier)
        })
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        toast.error(data.detail || 'Erreur prix');
        return;
      }
      
      setPricing(prev => ({ ...prev, selling_price: data.selling_price }));
      toast.success(`Prix de vente: ₪${data.selling_price}`);
      setCurrentStep(5);
    } catch (err) {
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  // Step 5: Publish
  const handleStep5 = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/sourcing/wizard/step5`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          product_id: productId,
          status: publishStatus,
          category_id: selectedCategory || null
        })
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        toast.error(data.detail || 'Erreur publication');
        return;
      }
      
      toast.success(data.message);
      
      // Show API info
      toast.info(`Pour automatiser: utilisez l'API ${data.api_required}`, { duration: 5000 });
      
      // Navigate to product
      navigate('/admin/sourcing');
    } catch (err) {
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  // Calculate selling price on input change
  useEffect(() => {
    if (pricing.source_price && pricing.margin_multiplier) {
      const selling = (parseFloat(pricing.source_price) * parseFloat(pricing.margin_multiplier)).toFixed(2);
      setPricing(prev => ({ ...prev, selling_price: parseFloat(selling) }));
    }
  }, [pricing.source_price, pricing.margin_multiplier]);

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="space-y-2">
              <Label className="text-white">URL du produit (ACBUY, Weidian, Taobao, etc.)</Label>
              <Input
                placeholder="https://www.acbuy.com/product?url=... ou https://weidian.com/item.html?itemID=..."
                value={sourceUrl}
                onChange={(e) => setSourceUrl(e.target.value)}
                className="bg-zinc-800 border-zinc-700 text-white"
                data-testid="wizard-url-input"
              />
            </div>
            
            <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
              <p className="text-sm text-blue-200">
                <strong>Sources supportées:</strong> ACBUY, KakoBuy, Weidian, Taobao, 1688, CNFans, Pandabuy
              </p>
            </div>
            
            {extractedData && (
              <div className="space-y-4">
                <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                  <div className="flex items-center gap-2 text-green-400 mb-2">
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-semibold">Lien extrait - {extractedData.source_type}</span>
                  </div>
                  <p className="text-xs text-gray-400 truncate">{extractedData.source_url}</p>
                </div>
                
                {extractedData.scraped && (
                  <div className={`p-4 rounded-lg border ${
                    extractedData.scraped.success 
                      ? 'bg-emerald-500/10 border-emerald-500/30' 
                      : 'bg-yellow-500/10 border-yellow-500/30'
                  }`}>
                    <div className="flex items-center gap-2 mb-3">
                      {extractedData.scraped.success ? (
                        <CheckCircle className="w-5 h-5 text-emerald-400" />
                      ) : (
                        <AlertCircle className="w-5 h-5 text-yellow-400" />
                      )}
                      <span className={`font-semibold ${
                        extractedData.scraped.success ? 'text-emerald-400' : 'text-yellow-400'
                      }`}>
                        {extractedData.scraped.success ? 'Données récupérées!' : 'Scraping partiel'}
                      </span>
                    </div>
                    
                    {extractedData.scraped.title && (
                      <div className="mb-2">
                        <p className="text-xs text-gray-400">Titre original:</p>
                        <p className="text-sm text-white font-medium">{extractedData.scraped.title}</p>
                      </div>
                    )}
                    
                    <div className="flex gap-4 text-sm">
                      {extractedData.scraped.price_cny && (
                        <span className="text-green-300">
                          💰 {extractedData.scraped.price_cny} CNY
                        </span>
                      )}
                      {extractedData.scraped.images_count > 0 && (
                        <span className="text-blue-300">
                          📷 {extractedData.scraped.images_count} photo(s)
                        </span>
                      )}
                    </div>
                    
                    {extractedData.scraped.error && (
                      <p className="text-xs text-yellow-300 mt-2">
                        ⚠️ {extractedData.scraped.error}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
            
            <Button 
              onClick={handleStep1} 
              disabled={loading || !sourceUrl.trim()}
              className="w-full bg-primary"
              data-testid="wizard-step1-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ArrowRight className="w-4 h-4 mr-2" />}
              Extraire et continuer
            </Button>
          </div>
        );
        
      case 2:
        return (
          <div className="space-y-6">
            {/* Afficher les images scrapées si disponibles */}
            {extractedData?.scraped?.images?.length > 0 && !imageUrls.trim() && (
              <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-emerald-400" />
                    <span className="font-semibold text-emerald-400">
                      {extractedData.scraped.images.length} photo(s) récupérée(s) automatiquement
                    </span>
                  </div>
                  <Button 
                    size="sm"
                    onClick={() => setImageUrls(extractedData.scraped.images.join('\n'))}
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    Utiliser ces photos
                  </Button>
                </div>
                <div className="grid grid-cols-5 gap-2">
                  {extractedData.scraped.images.slice(0, 5).map((img, idx) => (
                    <div key={idx} className="relative aspect-square bg-zinc-800 rounded overflow-hidden">
                      <img 
                        src={img} 
                        alt="" 
                        className="w-full h-full object-cover"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <div className="space-y-2">
              <Label className="text-white">URLs des photos (une par ligne)</Label>
              <Textarea
                placeholder={"https://img.alicdn.com/photo1.jpg\nhttps://img.alicdn.com/photo2.jpg"}
                value={imageUrls}
                onChange={(e) => setImageUrls(e.target.value)}
                className="bg-zinc-800 border-zinc-700 text-white min-h-[150px] font-mono text-sm"
                data-testid="wizard-images-input"
              />
            </div>
            
            <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
              <p className="text-sm text-blue-200">
                <strong>Note:</strong> {imageUrls.trim() ? 'Les URLs seront importées et converties en WebP.' : 
                  extractedData?.scraped?.images?.length > 0 ? 
                    'Cliquez sur "Utiliser ces photos" ou ajoutez vos propres URLs.' :
                    'Ajoutez des URLs de photos ou continuez sans images.'}
              </p>
            </div>
            
            {uploadedImages.length > 0 && (
              <div className="space-y-2">
                <Label className="text-white">Photos importées ({uploadedImages.length})</Label>
                <div className="grid grid-cols-4 gap-2">
                  {uploadedImages.map((img, idx) => (
                    <div key={idx} className="relative aspect-square bg-zinc-800 rounded overflow-hidden">
                      <img src={`${API_URL}${img}`} alt="" className="w-full h-full object-cover" />
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setCurrentStep(1)}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour
              </Button>
              <Button 
                onClick={handleStep2} 
                disabled={loading}
                className="flex-1 bg-primary"
                data-testid="wizard-step2-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ArrowRight className="w-4 h-4 mr-2" />}
                {imageUrls.trim() ? 'Importer les photos' : 
                  extractedData?.scraped?.images?.length > 0 ? 'Utiliser les photos scrapées' : 'Continuer sans photos'}
              </Button>
            </div>
          </div>
        );
        
      case 3:
        return (
          <div className="space-y-6">
            <Button 
              onClick={handleGenerateAI}
              disabled={loading}
              className="w-full bg-gradient-to-r from-purple-600 to-blue-600"
              data-testid="wizard-generate-ai-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
              Générer avec Claude IA
            </Button>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-white">Nom (Français) *</Label>
                <Input
                  value={aiContent.name_fr}
                  onChange={(e) => setAiContent(prev => ({ ...prev, name_fr: e.target.value }))}
                  className="bg-zinc-800 border-zinc-700 text-white"
                  placeholder="Ex: Premium Street Hoodie Oversized"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-white">Nom (Anglais)</Label>
                  <Input
                    value={aiContent.name_en}
                    onChange={(e) => setAiContent(prev => ({ ...prev, name_en: e.target.value }))}
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white">Nom (Hébreu)</Label>
                  <Input
                    value={aiContent.name_he}
                    onChange={(e) => setAiContent(prev => ({ ...prev, name_he: e.target.value }))}
                    className="bg-zinc-800 border-zinc-700 text-white"
                    dir="rtl"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-white">Description (Français) *</Label>
                <Textarea
                  value={aiContent.description_fr}
                  onChange={(e) => setAiContent(prev => ({ ...prev, description_fr: e.target.value }))}
                  className="bg-zinc-800 border-zinc-700 text-white min-h-[100px]"
                  placeholder="Description du produit (150 mots environ)"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-white">Description (EN)</Label>
                  <Textarea
                    value={aiContent.description_en}
                    onChange={(e) => setAiContent(prev => ({ ...prev, description_en: e.target.value }))}
                    className="bg-zinc-800 border-zinc-700 text-white min-h-[80px]"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white">Description (HE)</Label>
                  <Textarea
                    value={aiContent.description_he}
                    onChange={(e) => setAiContent(prev => ({ ...prev, description_he: e.target.value }))}
                    className="bg-zinc-800 border-zinc-700 text-white min-h-[80px]"
                    dir="rtl"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-white">Tags SEO (5 recommandés, séparés par des virgules)</Label>
                <Input
                  value={tagsInput}
                  onChange={(e) => setTagsInput(e.target.value)}
                  className="bg-zinc-800 border-zinc-700 text-white"
                  placeholder="streetwear, hoodie, premium, urban, fashion"
                />
              </div>
            </div>
            
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setCurrentStep(2)}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour
              </Button>
              <Button 
                onClick={handleStep3} 
                disabled={loading || !aiContent.name_fr.trim()}
                className="flex-1 bg-primary"
                data-testid="wizard-step3-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ArrowRight className="w-4 h-4 mr-2" />}
                Sauvegarder et continuer
              </Button>
            </div>
          </div>
        );
        
      case 4:
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-white">Prix source (CNY) *</Label>
                <Input
                  type="number"
                  value={pricing.source_price}
                  onChange={(e) => setPricing(prev => ({ ...prev, source_price: e.target.value }))}
                  className="bg-zinc-800 border-zinc-700 text-white"
                  placeholder="Ex: 150"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white">Multiplicateur de marge</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={pricing.margin_multiplier}
                  onChange={(e) => setPricing(prev => ({ ...prev, margin_multiplier: e.target.value }))}
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
              </div>
            </div>
            
            <div className="p-6 bg-gradient-to-r from-green-900/30 to-emerald-900/30 rounded-lg border border-green-500/30">
              <div className="text-center">
                <p className="text-gray-400 text-sm mb-1">Prix de vente calculé</p>
                <p className="text-4xl font-bold text-green-400">₪{pricing.selling_price || '0.00'}</p>
                <p className="text-sm text-gray-400 mt-2">
                  {pricing.source_price} CNY × {pricing.margin_multiplier} = ₪{pricing.selling_price}
                </p>
              </div>
            </div>
            
            <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
              <p className="text-sm text-blue-200">
                <strong>Note:</strong> Les variantes par défaut (tailles EU 40-45) seront créées automatiquement.
                Vous pourrez les modifier depuis la fiche produit.
              </p>
            </div>
            
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setCurrentStep(3)}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour
              </Button>
              <Button 
                onClick={handleStep4} 
                disabled={loading || !pricing.source_price}
                className="flex-1 bg-primary"
                data-testid="wizard-step4-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ArrowRight className="w-4 h-4 mr-2" />}
                Continuer
              </Button>
            </div>
          </div>
        );
        
      case 5:
        return (
          <div className="space-y-6">
            <div className="space-y-4">
              <Label className="text-white">Catégorie (optionnel)</Label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full p-3 bg-zinc-800 border border-zinc-700 rounded-md text-white"
              >
                <option value="">Aucune catégorie</option>
                {categories.map(cat => (
                  <option key={cat._id || cat.id} value={cat._id || cat.id}>
                    {cat.emoji || ''} {cat.name?.fr || cat.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="space-y-4">
              <Label className="text-white">Statut de publication</Label>
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => setPublishStatus('draft')}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    publishStatus === 'draft' 
                      ? 'border-yellow-500 bg-yellow-500/10' 
                      : 'border-zinc-700 bg-zinc-800/50'
                  }`}
                >
                  <Save className={`w-8 h-8 mx-auto mb-2 ${publishStatus === 'draft' ? 'text-yellow-400' : 'text-gray-400'}`} />
                  <p className={`font-semibold ${publishStatus === 'draft' ? 'text-yellow-400' : 'text-white'}`}>Brouillon</p>
                  <p className="text-xs text-gray-400 mt-1">Modifier avant publication</p>
                </button>
                
                <button
                  onClick={() => setPublishStatus('published')}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    publishStatus === 'published' 
                      ? 'border-green-500 bg-green-500/10' 
                      : 'border-zinc-700 bg-zinc-800/50'
                  }`}
                >
                  <Rocket className={`w-8 h-8 mx-auto mb-2 ${publishStatus === 'published' ? 'text-green-400' : 'text-gray-400'}`} />
                  <p className={`font-semibold ${publishStatus === 'published' ? 'text-green-400' : 'text-white'}`}>Publier</p>
                  <p className="text-xs text-gray-400 mt-1">Visible sur le site</p>
                </button>
              </div>
            </div>
            
            <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-purple-400 mt-0.5" />
                <div>
                  <p className="text-sm text-purple-200 font-semibold">API pour automatisation</p>
                  <p className="text-sm text-gray-300 mt-1">
                    Pour automatiser complètement ce flux (extraction de données, photos, prix), 
                    vous devez souscrire à l'API <strong className="text-purple-300">OTCommerce</strong>.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setCurrentStep(4)}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour
              </Button>
              <Button 
                onClick={handleStep5} 
                disabled={loading}
                className={`flex-1 ${publishStatus === 'published' ? 'bg-green-600 hover:bg-green-700' : 'bg-yellow-600 hover:bg-yellow-700'}`}
                data-testid="wizard-step5-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                {publishStatus === 'published' ? 'Publier le produit' : 'Enregistrer en brouillon'}
              </Button>
            </div>
          </div>
        );
        
      default:
        return null;
    }
  };

  return (
    <AdminLayout>
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin/sourcing')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-white">Import de produit</h1>
            <p className="text-gray-400">Assistant d'import en 5 étapes</p>
          </div>
        </div>
        
        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {STEPS.map((step, index) => (
              <div key={step.id} className="flex items-center">
                <div className={`flex flex-col items-center ${index < STEPS.length - 1 ? 'w-full' : ''}`}>
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all ${
                    currentStep > step.id 
                      ? 'bg-green-500 border-green-500 text-white' 
                      : currentStep === step.id 
                        ? 'bg-primary border-primary text-white' 
                        : 'bg-zinc-800 border-zinc-700 text-gray-500'
                  }`}>
                    {currentStep > step.id ? (
                      <Check className="w-5 h-5" />
                    ) : (
                      <step.icon className="w-5 h-5" />
                    )}
                  </div>
                  <span className={`text-xs mt-2 ${currentStep === step.id ? 'text-primary' : 'text-gray-500'}`}>
                    {step.name}
                  </span>
                </div>
                {index < STEPS.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-2 ${currentStep > step.id ? 'bg-green-500' : 'bg-zinc-700'}`} />
                )}
              </div>
            ))}
          </div>
        </div>
        
        {/* Step Content */}
        <Card className="bg-zinc-900/60 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              {STEPS[currentStep - 1] && (() => {
                const StepIcon = STEPS[currentStep - 1].icon;
                return <StepIcon className="w-5 h-5 text-primary" />;
              })()}
              Étape {currentStep}: {STEPS[currentStep - 1]?.name}
            </CardTitle>
            <CardDescription>{STEPS[currentStep - 1]?.description}</CardDescription>
          </CardHeader>
          <CardContent>
            {renderStepContent()}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}

export default AdminImportWizardPage;
